import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { TemaService } from '../../../core/services/tema.service';
import { TituloNavbarService } from '../../../core/services/titulo-navbar.service';
import { ControleSessaoService } from '../../../core/services/controle-sessao.service';

import { MenuItem } from '../../../core/models/menu-item.model';
import { Modulo, Sistema } from '../../../core/models/usuario-login.model';

import {
  CARDS_MODULOS_CONFIG,
  CARD_TEXTO_PADRAO,
  CARD_DEFAULT_VARIANT,
  CardVariant,
  CardModuloConfig,
} from '../../../core/config/cards-modulos.config';

@Component({
  selector: 'app-modulos-home',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './modulos-home.component.html',
  styleUrl: './modulos-home.component.scss',
})
export class ModulosHomeComponent implements OnInit, OnDestroy {
  modulos: Sistema[] = [];
  modulosFiltrados: Sistema[] = [];
  termoBusca: string = '';

  idsFixados = new Set<string>();
  isDarkMode = false;

  private subTema?: Subscription;

  constructor(
    private sessao: ControleSessaoService,
    private router: Router,
    private temaService: TemaService,
    private tituloNavbarService: TituloNavbarService
  ) {}

  ngOnInit(): void {
    this.tituloNavbarService.setTitulo('Meus módulos');

    const usuario = this.sessao.getUsuarioSessao();

    this.modulos = (usuario?.sistemas || []).map((s: any) => ({
      ...s,
      modulos: Array.isArray(s?.modulos) ? s.modulos : [],
    })) as Sistema[];

    this.modulosFiltrados = this.modulos;

    // menu sidebar dinâmico
    const menuSidebar = this.gerarMenuSidebar();
    localStorage.setItem('menu_sidebar', JSON.stringify(menuSidebar));
    window.dispatchEvent(new CustomEvent('menu-sidebar-atualizado'));

    // fixados
    this.carregarEstadoFixos();

    // tema
    this.isDarkMode = this.temaService.estaEscuro();
    this.subTema = this.temaService.modoEscuro$.subscribe((modoEscuro) => {
      this.isDarkMode = modoEscuro;
    });

    this.reaplicarFiltroEOrdenacao();
  }

  ngOnDestroy(): void {
    this.subTema?.unsubscribe();
  }

  // ===== Busca =====
  buscar(): void {
    const termo = this.termoBusca.trim().toLowerCase();

    if (!termo) {
      this.modulosFiltrados = this.modulos;
      this.reaplicarFiltroEOrdenacao();
      return;
    }

    this.modulosFiltrados = (this.modulos || [])
      .map((sistema: any) => {
        const lista = Array.isArray(sistema?.modulos) ? sistema.modulos : [];
        const filtrados = lista.filter((modulo: any) =>
          String(modulo?.nome || '')
            .toLowerCase()
            .includes(termo)
        );
        return { ...sistema, modulos: filtrados };
      })
      .filter((s: any) => Array.isArray(s?.modulos) && s.modulos.length > 0);

    this.reaplicarFiltroEOrdenacao();
  }

  // ===== Ordenação =====
  reaplicarFiltroEOrdenacao(): void {
    this.modulosFiltrados = (this.modulosFiltrados || []).map((grupo: any) => {
      const lista = Array.isArray(grupo?.modulos) ? grupo.modulos : [];

      const ordenados = [...lista].sort((a: any, b: any) => {
        const ap = this.estaFixado(a) ? 0 : 1;
        const bp = this.estaFixado(b) ? 0 : 1;
        if (ap !== bp) return ap - bp;
        return String(a?.nome || '').localeCompare(String(b?.nome || ''));
      });

      return { ...grupo, modulos: ordenados };
    });
  }

  // ===== Fixar / Desfixar =====
  estaFixado(mod: Modulo): boolean {
    const chave = this.obterChaveModulo(mod);
    return this.idsFixados.has(chave);
  }

  alternarFixacao(mod: Modulo): void {
    const chave = this.obterChaveModulo(mod);

    if (this.idsFixados.has(chave)) this.idsFixados.delete(chave);
    else this.idsFixados.add(chave);

    this.salvarEstadoFixos();
    window.dispatchEvent(new CustomEvent('modulos-fixados-atualizados'));

    this.reaplicarFiltroEOrdenacao();
  }

  private obterChaveModulo(mod: Modulo): string {
    const anyMod: any = mod as any;

    if (anyMod?.id !== null && anyMod?.id !== undefined) {
      return String(anyMod.id);
    }

    const caminho = String(anyMod?.caminho_exibicao || '').replace(
      /^\/|\/$/g,
      ''
    );
    return `${String(anyMod?.nome || 'modulo')}::${caminho}`;
  }

  // ===== Persistência fixos =====
  private obterIdUsuario(): string {
    const u: any = this.sessao.getUsuarioSessao();

    const candidato =
      u?.id ??
      u?.usuario?.id ??
      u?.usuarioId ??
      u?.usuario ??
      u?.email ??
      u?.login ??
      u?.nomeUsuario ??
      u?.cpf ??
      null;

    return String(candidato ?? 'anon');
  }

  private chavesStorage() {
    const uid = this.obterIdUsuario();
    return { fixos: `modulos_fixados::${uid}` };
  }

  private carregarEstadoFixos(): void {
    const { fixos } = this.chavesStorage();
    try {
      const saved = JSON.parse(localStorage.getItem(fixos) || '[]');
      if (Array.isArray(saved)) this.idsFixados = new Set(saved.map(String));
      else this.idsFixados = new Set<string>();
    } catch {
      this.idsFixados = new Set<string>();
    }
  }

  private salvarEstadoFixos(): void {
    const { fixos } = this.chavesStorage();
    localStorage.setItem(fixos, JSON.stringify(Array.from(this.idsFixados)));
  }

  // ===== Clique no card =====
  acessarModulo(moduloSelecionado: Modulo): void {
    localStorage.setItem(
      'modulo_selecionado',
      JSON.stringify(moduloSelecionado)
    );

    const menuSidebar: MenuItem[] = JSON.parse(
      localStorage.getItem('menu_sidebar') || '[]'
    );

    const item = menuSidebar.find(
      (m: any) => m?.moduloCompleto?.id === (moduloSelecionado as any)?.id
    );

    if (item?.path) {
      this.router.navigateByUrl(item.path);
      return;
    }

    const primeiroComponente = (moduloSelecionado as any)?.componentes?.[0];
    if (primeiroComponente) {
      const caminhoFinal = `${(moduloSelecionado as any).caminho_exibicao}/${
        primeiroComponente.caminho_exibicao
      }`.replace(/\/+/g, '/');

      this.router.navigateByUrl(caminhoFinal);
    } else {
      console.warn('Módulo sem componentes válidos.');
    }
  }

  // ===== Menu da sidebar =====
  private gerarMenuSidebar(): MenuItem[] {
    const menu: MenuItem[] = [];

    const norm = (p: string) => (p || '').trim().replace(/\/+/g, '/');
    const strip = (p: string) => norm(p).replace(/^\/|\/$/g, '');
    const abs = (p: string) => (p.startsWith('/') ? norm(p) : `/${norm(p)}`);

    const join = (...parts: string[]) => {
      const full = parts
        .filter(Boolean)
        .map((x) => strip(x))
        .filter(Boolean)
        .join('/');
      return `/${full}`.replace(/\/+/g, '/');
    };

    const isRotaClicavel = (rota: string) => !rota.includes('/:');

    const resolveFrontPath = (modulo: any, componente?: any, funcao?: any) => {
      const moduloBase = strip(modulo?.caminho_exibicao || '');
      const componenteBase = strip(componente?.caminho_exibicao || '');

      const rotaFuncao = String(funcao?.rota || '').trim();
      if (rotaFuncao) {
        const rotaAbs = abs(rotaFuncao);
        if (moduloBase && rotaAbs.startsWith(`/${moduloBase}`)) return rotaAbs;
        if (moduloBase) return join(moduloBase, rotaAbs);
        return rotaAbs;
      }

      if (moduloBase && componenteBase) {
        if (componenteBase === moduloBase) return `/${moduloBase}`;
        return join(moduloBase, componenteBase);
      }

      if (moduloBase) return join(moduloBase);
      return '/modulos';
    };

    (this.modulos || []).forEach((sistema: Sistema) => {
      const listaModulos = Array.isArray((sistema as any)?.modulos)
        ? (sistema as any).modulos
        : [];

      listaModulos.forEach((modulo: any) => {
        const subItems: any[] = [];

        const componentes = Array.isArray(modulo?.componentes)
          ? modulo.componentes
          : [];

        componentes.forEach((componente: any) => {
          const funcoes = Array.isArray(componente?.funcoes)
            ? componente.funcoes
            : [];

          if (funcoes.length) {
            funcoes.forEach((funcao: any) => {
              const rota = String(funcao?.rota || '').trim();
              if (!rota) return;
              if (!isRotaClicavel(rota)) return;

              subItems.push({
                titulo: this.formatarTitulo(funcao?.nome || 'Opção'),
                path: resolveFrontPath(modulo, componente, funcao),
              });
            });
            return;
          }

          subItems.push({
            titulo: this.formatarTitulo(componente?.nome || 'Componente'),
            path: resolveFrontPath(modulo, componente),
          });
        });

        const defaultPath =
          subItems[0]?.path || resolveFrontPath(modulo, componentes?.[0]);

        menu.push({
          icon: `${modulo?.icone || 'icon-default'}.png`,
          titulo: String(modulo?.nome || 'Módulo'),
          path: defaultPath,
          subItems,
          moduloCompleto: modulo,
        });
      });
    });

    return menu;
  }

  private formatarTitulo(nome: string): string {
    return (nome || '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase());
  }

  // ===== Cards config =====
  private normalizar(p: string): string {
    return (p || '').trim().replace(/\/+/g, '/').replace(/\/$/, '');
  }

  private obterCaminhoModulo(modulo: any): string {
    return this.normalizar(String(modulo?.caminho_exibicao || ''));
  }

  private obterIdModulo(modulo: any): string {
    const id = modulo?.id ?? modulo?.modulo_id ?? null;
    return id === null || id === undefined ? '' : String(id);
  }

  private obterNomeModulo(modulo: any): string {
    return String(modulo?.nome || '')
      .trim()
      .toLowerCase();
  }

  private acharConfigModulo(modulo: any): CardModuloConfig | undefined {
    const id = this.obterIdModulo(modulo);
    const caminho = this.obterCaminhoModulo(modulo);
    const nome = this.obterNomeModulo(modulo);

    return CARDS_MODULOS_CONFIG.find((c) => {
      if (c.id !== undefined && c.id !== null && id && String(c.id) === id)
        return true;
      if (c.caminho && this.normalizar(c.caminho) === caminho) return true;
      if (c.nome && String(c.nome).trim().toLowerCase() === nome) return true;
      return false;
    });
  }

  getCardTexto(modulo: any): string {
    return this.acharConfigModulo(modulo)?.texto || CARD_TEXTO_PADRAO;
  }

  getCardVariant(modulo: any): CardVariant {
    const cfg = this.acharConfigModulo(modulo);
    if (cfg?.variant) return cfg.variant;
    return CARD_DEFAULT_VARIANT;
  }
}
